<template>
  <el-dialog
      :model-value="modelValue"
      :title="isEdit ? 'Ko\'rsatkichni tahrirlash' : 'Yangi ko\'rsatkich'"
      width="600px"
      destroy-on-close
      @update:model-value="$emit('update:modelValue', $event)"
  >
    <div v-loading="saving" class="sbs-row-form">
      <div class="sbs-field">
        <label class="sbs-field__label">Nomi <span class="sbs-req">*</span></label>
        <el-tabs v-model="titleTab" class="sbs-lang-tabs">
          <el-tab-pane v-for="lt in langTabs" :key="lt.code" :name="lt.code">
            <template #label>
              <span class="sbs-lang-tab-label">
                {{ lt.tabLabel }}
                <span class="sbs-lang-dot" :class="{'is-filled': !!form.title[lt.code]}"/>
              </span>
            </template>
            <el-input v-model="form.title[lt.code]" placeholder="Masalan: Qishloq xo'jaligi subsidiyalari"/>
          </el-tab-pane>
        </el-tabs>
      </div>

      <div class="sbs-field">
        <label class="sbs-field__label">
          Izoh <span class="sbs-hint">— footnote, nom yoniga * bo'lib chiqadi</span>
        </label>
        <el-tabs v-model="noteTab" class="sbs-lang-tabs">
          <el-tab-pane v-for="lt in langTabs" :key="lt.code" :name="lt.code">
            <template #label>
              <span class="sbs-lang-tab-label">
                {{ lt.tabLabel }}
                <span class="sbs-lang-dot" :class="{'is-filled': !!form.note[lt.code]}"/>
              </span>
            </template>
            <el-input v-model="form.note[lt.code]" placeholder="Ixtiyoriy"/>
          </el-tab-pane>
        </el-tabs>
      </div>

      <div class="sbs-field">
        <label class="sbs-field__label">Ota-ko'rsatkich</label>
        <el-tree-select
            v-model="form.parent_id"
            :data="parentTreeOptions"
            :props="{label: 'label', children: 'children', value: 'id'}"
            node-key="id"
            check-strictly
            clearable
            placeholder="— Ildiz —"
            class="sbs-w-full"
        />
      </div>

      <el-row :gutter="16">
        <el-col :span="8">
          <div class="sbs-field">
            <label class="sbs-field__label">Tartib</label>
            <el-input-number v-model="form.order" :min="0" :step="1" class="sbs-w-full" @change="orderTouched = true"/>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="sbs-field">
            <label class="sbs-field__label">Qalin</label>
            <el-switch v-model="form.is_bold"/>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="sbs-field">
            <label class="sbs-field__label">Holati</label>
            <el-switch
                v-model="form.is_active"
                active-text="Faol"
                inactive-text="Nofaol"
                style="--el-switch-on-color: #16A34A"
            />
          </div>
        </el-col>
      </el-row>
    </div>

    <template #footer>
      <el-button @click="$emit('update:modelValue', false)">Bekor qilish</el-button>
      <el-button type="primary" :loading="saving" @click="onSave">Saqlash</el-button>
    </template>
  </el-dialog>
</template>

<script>
import {ElMessage, ElNotification} from "element-plus";
import {API_BASE, LANG_TABS, emptyMultiLang, fillMultiLang, multiLangOrNull, displayLangText} from "./subsidiesTypes";

export default {
  name: "SubsidiesRowDialog",
  props: {
    modelValue: {type: Boolean, default: false},
    row: {type: Object, default: null},
    parentId: {type: Number, default: null},
    rowsTree: {type: Array, default: () => []},
  },
  emits: ["update:modelValue", "saved"],
  data() {
    return {
      langTabs: LANG_TABS,
      titleTab: "uz_lat",
      noteTab: "uz_lat",
      saving: false,
      orderTouched: false,
      form: this.buildForm(null, null),
    };
  },
  computed: {
    isEdit() {
      return !!this.row?.id;
    },
    // Excludes the row being edited and its whole subtree — CONSOLE.md §8.4 cycle guard.
    parentTreeOptions() {
      const excludeId = this.row?.id ?? null;
      const map = (nodes) => nodes
          .filter((n) => n.id !== excludeId)
          .map((n) => ({
            id: n.id,
            label: displayLangText(n.title) || "...",
            children: n.children?.length ? map(n.children) : undefined,
          }));
      return map(this.rowsTree);
    },
  },
  watch: {
    modelValue(val) {
      if (val) {
        this.form = this.buildForm(this.row, this.parentId);
        this.titleTab = "uz_lat";
        this.noteTab = "uz_lat";
        this.orderTouched = false;
        if (!this.isEdit) this.form.order = this.computeNextOrder(this.form.parent_id);
      }
    },
    "form.parent_id"(value) {
      if (!this.isEdit && !this.orderTouched) {
        this.form.order = this.computeNextOrder(value);
      }
    },
  },
  methods: {
    buildForm(row, parentId) {
      return {
        title: fillMultiLang(row?.title),
        note: fillMultiLang(row?.note),
        parent_id: row ? (row.parent_id ?? null) : (parentId ?? null),
        order: row?.order ?? 10,
        is_bold: row?.is_bold || false,
        is_active: row?.is_active !== false,
      };
    },
    siblingsOf(parentId) {
      if (parentId == null) return this.rowsTree;
      const find = (nodes) => {
        for (const n of nodes) {
          if (n.id === parentId) return n.children || [];
          const hit = n.children?.length ? find(n.children) : null;
          if (hit) return hit;
        }
        return null;
      };
      return find(this.rowsTree) || [];
    },
    // Yangi ko'rsatkich oxirgi qo'shni ko'rsatkichdan keyin tursin — har safar 10 emas.
    computeNextOrder(parentId) {
      const orders = this.siblingsOf(parentId)
          .filter((n) => n.id !== this.row?.id)
          .map((n) => Number(n.order))
          .filter((v) => Number.isFinite(v));
      return orders.length ? Math.max(...orders) + 1 : 1;
    },
    validate() {
      const missing = this.langTabs.filter((lt) => !this.form.title[lt.code]?.trim());
      if (missing.length) {
        ElMessage({type: "warning", message: "Nomni barcha 5 tilda to'ldiring"});
        this.titleTab = missing[0].code;
        return false;
      }
      return true;
    },
    onSave() {
      if (!this.validate()) return;
      const payload = {
        title: this.form.title,
        note: multiLangOrNull(this.form.note) || emptyMultiLang(),
        parent_id: this.form.parent_id ?? null,
        order: this.form.order,
        is_active: this.form.is_active,
        is_bold: this.form.is_bold,
      };
      this.saving = true;
      const request = this.isEdit
          ? this.$http.put(`${API_BASE}/rows/${this.row.id}`, payload)
          : this.$http.post(`${API_BASE}/rows`, payload);
      request
          .then(() => {
            ElMessage({type: "success", message: this.isEdit ? "Ko'rsatkich yangilandi" : "Ko'rsatkich yaratildi"});
            this.$emit("saved");
            this.$emit("update:modelValue", false);
          })
          .catch((error) => {
            ElNotification({
              title: "Saqlashda xatolik",
              message: error?.response?.data?.message || "Iltimos, qayta urinib ko'ring.",
              type: "error",
            });
          })
          .finally(() => {
            this.saving = false;
          });
    },
  },
};
</script>

<style scoped lang="scss">
.sbs-row-form {
  max-height: 65vh;
  overflow-y: auto;
  padding-right: 4px;
}

.sbs-field {
  margin-bottom: 16px;

  &__label {
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #303133;
  }
}

.sbs-req {
  color: #e11d48;
}

.sbs-hint {
  font-weight: 400;
  font-size: 12px;
  color: #8a94a6;
}

.sbs-w-full {
  width: 100%;
}

.sbs-lang-tabs {
  :deep(.el-tabs__header) {
    margin-bottom: 8px;
  }
}

.sbs-lang-tab-label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.sbs-lang-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #d0d5dd;

  &.is-filled {
    background: #16a34a;
  }
}
</style>
